import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Arshjul } from "./Arshjul";
import { AiAnnualPlanDialog } from "./AiAnnualPlanDialog";
import { WHEEL_MONTHS, CUR_YEAR, CUR_MONTH, MonthSelect } from "./shared";

export function AnnualWheelSection({ companyId, meetings, onApplied }) {
  const navigate = useNavigate();
  const [wheelYear, setWheelYear] = useState(CUR_YEAR);
  const [points, setPoints] = useState(null);
  const [pointOpen, setPointOpen] = useState(false);
  const [pointForm, setPointForm] = useState({ title: "", month: CUR_MONTH });
  const [savingPoint, setSavingPoint] = useState(false);

  const loadPoints = useCallback(() => api.get(`/companies/${companyId}/annual-wheel`).then((r) => setPoints(r.data)).catch(() => setPoints([])), [companyId]);

  useEffect(() => {
    setPoints(null);
    loadPoints();
  }, [loadPoints]);

  const addPoint = async () => {
    if (!pointForm.title.trim()) {
      toast.error("Skriv inn en tittel");
      return;
    }
    setSavingPoint(true);
    try {
      await api.post(`/companies/${companyId}/annual-wheel`, { title: pointForm.title.trim(), month: pointForm.month });
      toast.success("Punkt lagt til på årshjulet");
      setPointOpen(false);
      setPointForm({ title: "", month: CUR_MONTH });
      loadPoints();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPoint(false);
    }
  };

  const delPoint = async (id) => {
    try {
      await api.delete(`/companies/${companyId}/annual-wheel/${id}`);
      loadPoints();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const years = useMemo(() => {
    const s = new Set((meetings || []).map((m) => new Date(m.date).getFullYear()));
    s.add(CUR_YEAR);
    return [...s].sort((a, b) => a - b);
  }, [meetings]);
  const wheelMeetings = (meetings || []).filter((m) => new Date(m.date).getFullYear() === wheelYear);
  const recurringPoints = (points || []).filter((p) => !p.auto);
  const wheelPoints = (points || []).filter((p) => !p.year || Number(p.year) === wheelYear);

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm lg:col-span-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-headings text-lg font-bold tracking-tight">Årshjul</h2>
          <p className="text-xs text-muted-foreground">Klikk en måned med møte for å åpne det</p>
        </div>
        <ToggleGroup type="single" value={String(wheelYear)} onValueChange={(v) => v && setWheelYear(Number(v))} className="rounded-lg bg-muted p-1" data-testid="arshjul-year">
          {years.map((y) => (
            <ToggleGroupItem key={y} value={String(y)} data-testid={`arshjul-year-${y}`} className="h-7 px-3 text-xs data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow">{y}</ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {meetings === null ? (
        <div className="grid place-items-center py-4"><Skeleton className="h-[300px] w-[300px] rounded-full" /></div>
      ) : (
        <Arshjul year={wheelYear} meetings={wheelMeetings} points={wheelPoints} onSelect={(m) => navigate(`/selskap/${companyId}/moter/${m.id}`)} />
      )}
      {meetings && wheelMeetings.length === 0 ? <p className="mt-2 text-center text-xs text-muted-foreground">Ingen møter i {wheelYear} ennå.</p> : null}

      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Signert</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Mangler signatur</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Fast punkt</span>
      </div>

      <div className="mt-5 border-t border-border/60 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Faste punkter</h3>
            <p className="text-xs text-muted-foreground">Årlige punkter som vises på hjulet hvert år.</p>
          </div>
          <div className="flex items-center gap-2">
          <AiAnnualPlanDialog companyId={companyId} year={wheelYear} onApplied={() => { onApplied?.(); loadPoints(); }} />
          <Dialog open={pointOpen} onOpenChange={setPointOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="add-wheel-point-btn"><Plus className="h-3.5 w-3.5" /> Legg til punkt</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nytt fast punkt</DialogTitle>
                <DialogDescription>Legg til et årlig punkt på hjulet, f.eks. «Q1-regnskap» eller «Generalforsamling».</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Tittel</Label>
                  <Input data-testid="wheel-point-title-input" className="mt-1.5" value={pointForm.title} onChange={(e) => setPointForm({ ...pointForm, title: e.target.value })} placeholder="Q1-regnskap" />
                </div>
                <MonthSelect label="Måned" testid="wheel-point-month" value={pointForm.month} onChange={(mn) => setPointForm({ ...pointForm, month: mn })} />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPointOpen(false)}>Avbryt</Button>
                <Button data-testid="save-wheel-point-btn" onClick={addPoint} disabled={savingPoint}>{savingPoint ? "Lagrer …" : "Legg til"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="wheel-points-list">
          {points === null ? (
            <Skeleton className="h-7 w-44 rounded-full" />
          ) : recurringPoints.length === 0 ? (
            <p className="text-xs text-muted-foreground">Ingen faste punkter ennå. Legg til f.eks. regnskaps- og rapporteringsfrister.</p>
          ) : (
            recurringPoints.map((p) => (
              <span key={p.id} data-testid={`wheel-point-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 py-1 pl-2.5 pr-1 text-xs font-medium text-blue-700 dark:text-blue-300">
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{WHEEL_MONTHS[Math.max(1, Math.min(12, p.month)) - 1]}</span>
                {p.title}
                <button data-testid={`del-wheel-point-${p.id}`} onClick={() => delPoint(p.id)} className="grid h-4 w-4 place-items-center rounded-full text-blue-700/60 transition-colors hover:bg-blue-500/20 hover:text-blue-700 dark:text-blue-300/60 dark:hover:text-blue-300" title="Fjern punkt"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
